<template>
  <div v-if="category" class="p-2 text-base font-semibold leading-tight">
    {{
      t(`note.${props.insuranceCase.insuranceProvider}`, {
        category,
      })
    }}
    <Divider />
  </div>

  <div class="grid grid-cols-3 gap-4">
    <FilterDropdowns
      :category-filter="categoryFilter"
      :make-filter="makeFilter"
      class="col-span-2"
      @change="handleFilterChange"
    />
    <Select
      v-model="onlyClosestToDeliveryLocation"
      type="number"
      variant="ghost"
      :options="onlyClosestToDeliveryLocationOptions"
    />
  </div>

  <VehiclesList
    :create-booking-props="createBookingProps"
    class="mt-8"
    :only-closest-to-delivery-location="!!onlyClosestToDeliveryLocation"
    @select-offer="handleSelectOffer"
  />

  <CreateBookingModal
    v-if="pickupLocation && dropoffLocation && selectedOffer"
    v-model:show-modal="showModal"
    :create-booking-props="createBookingProps"
  />
</template>

<script lang="ts" setup>
import VehiclesList from '../booking-create/components/VehiclesList.vue';
import { computed, ref, watch } from 'vue';
import CreateBookingModal from '../booking-create/components/CreateBookingModal.vue';
import type { VehicleCategory } from '@/entities/vehicle-category.enum';
import type {
  InsuranceCase,
  JlrInsuranceData,
  Offer,
  VolvoInsuranceData,
} from '@carvia/ros-client-types';
import FilterDropdowns from '@/views/bookings/booking-create/components/FilterDropdowns.vue';
import { useCreateBookingProps } from '@/views/bookings/booking-create/components/use-create-booking-props';
import { asShortTermPricing } from '@/utils/pricing.utils';
import { locationGeocodedAddressToBookingLocation } from '@/utils/location-geocoded-address-to-booking-location';
import { BookingLocationType } from '@/entities/bookings/booking-location-type.enum';
import { CUSTOM_LOCATIONS } from '@/constants/custom-locations.constant';
import { getOperationalStationOfCar } from '@/hooks/use-current-station-of-car';
import type { Car } from '@/entities/cars/car.entity';
import Select from '@/components/Select.vue';
import { useI18n } from 'vue-i18n';
import Divider from '@/components/Divider.vue';
import { mapApiService } from '@/api/map-api-http-service';

const props = defineProps<{
  insuranceCase: InsuranceCase;
}>();

const { t } = useI18n();

const createBookingProps = useCreateBookingProps({
  startDate: props.insuranceCase.startDate ? new Date(props.insuranceCase.startDate) : undefined,
  endDate: props.insuranceCase.endDate ? new Date(props.insuranceCase.endDate) : undefined,
  insuranceCase: props.insuranceCase,
});
const {
  startDate,
  pickupLocation,
  dropoffLocation,
  categoryFilter,
  makeFilter,
  selectedOffer,
  pricing,
} = createBookingProps;

// Reverse geocode the insurance case location if it has no country, so the full
// address (which the backend requires for street pickup locations) is sent along
// with the create booking requests.
watch(
  () => props.insuranceCase.locationGeocodedAddress,
  async (locationGeocodedAddress) => {
    const bookingLocation = locationGeocodedAddressToBookingLocation(locationGeocodedAddress);
    if (!bookingLocation) {
      pickupLocation.value = null;
      return;
    }
    pickupLocation.value = bookingLocation;

    const location = bookingLocation.location;
    const coordinates = location?.coordinates;
    if (location?.address?.country || !coordinates) {
      return;
    }

    try {
      const geocoded = await mapApiService.getPositionByCoordinates(coordinates);
      pickupLocation.value = {
        ...bookingLocation,
        location: {
          ...location,
          address: {
            ...geocoded.address,
            ...location.address,
            displayName: location.address?.displayName ?? geocoded.address?.displayName ?? '',
          },
        },
      };
    } catch {
      // Keep the non-enriched location if reverse geocoding fails.
    }
  },
  { immediate: true },
);

const showModal = ref(false);

const onlyClosestToDeliveryLocationOptions = computed(() => [
  { label: t('onlyClosestToDeliveryLocation'), value: 1 },
  { label: t('showAllCars'), value: 0 },
]);

const onlyClosestToDeliveryLocation = ref(1);

const handleFilterChange = (category: VehicleCategory | null, make: string | null) => {
  categoryFilter.value = category;
  makeFilter.value = make;
};

const handleSelectOffer = (offer: Offer) => {
  selectedOffer.value = offer;
  pricing.value = asShortTermPricing(offer.pricing);
  dropoffLocation.value = {
    locationType: BookingLocationType.STATION,
    station: getOperationalStationOfCar(
      offer.car as unknown as Car,
      startDate.value,
      createBookingProps.station.value && createBookingProps.station.value !== CUSTOM_LOCATIONS
        ? [createBookingProps.station.value.id]
        : undefined,
    ),
    location: null,
  };
  showModal.value = true;
};

watch([showModal], () => {
  if (!showModal.value) {
    dropoffLocation.value = null;
  }
});

const category = computed(() => {
  switch (props.insuranceCase.insuranceProvider) {
    case 'volvo': {
      const category = (props.insuranceCase.insuranceData as VolvoInsuranceData).volvoCategory;
      return category ? t(`volvoCategories.${category}`) : undefined;
    }
    case 'jlr': {
      const category = (props.insuranceCase.insuranceData as JlrInsuranceData).jlrCategory;
      return category ? t(`jlrCategories.${category}`) : undefined;
    }
    default:
      return undefined;
  }
});
</script>

<i18n lang="json">
{
  "en": {
    "onlyClosestToDeliveryLocation": "Only cars from closest stations",
    "showAllCars": "Include all stations",
    "note": {
      "volvo": "Note: Tariff {category} was booked in the claim. Please take a car from this tariff if available.",
      "jlr": "Note: Tariff {category} was booked in the claim. Please take a car from this tariff if available."
    }
  },
  "de": {
    "onlyClosestToDeliveryLocation": "Nur Autos von nächsten Stationen",
    "showAllCars": "Alle Stationen einbeziehen",
    "note": {
      "volvo": "Hinweis: In der KÜ wurde Tarif {category} gebucht - Bitte Fahrzeug aus diesem Tarif nehmen, wenn vefügbar.",
      "jlr": "Hinweis: In der KÜ wurde Tarif {category} gebucht - Bitte Fahrzeug aus diesem Tarif nehmen, wenn vefügbar."
    }
  },
  "es": {
    "onlyClosestToDeliveryLocation": "Solo coches de las estaciones más cercanas",
    "showAllCars": "Incluir todas las estaciones",
    "note": {
      "volvo": "Nota: en el parte se reservó la tarifa {category}. Elija un vehículo de esa tarifa si hay disponible.",
      "jlr": "Nota: en el parte se reservó la tarifa {category}. Elija un vehículo de esa tarifa si hay disponible."
    }
  }
}
</i18n>
